import { useState, useCallback } from 'react';
import { logErrorCtx } from 'app/logging';
import {
  deleteSalesOrders as deleteSalesOrdersService,
  unIssueSalesOrders as unIssueSalesOrdersService,
  multipleIssueSalesOrders as issueSalesOrdersService,
} from 'services/salesOrders';

export interface UseSalesOrderBulkActionsProps {
  selectedIds: number[];
  fetchSearchResult: () => Promise<void> | void;
  onSuccess?: () => void;
  onError?: (error: any, actionType: SalesOrderBulkActionKey) => void;
}

export type SalesOrderBulkActionKey = 'delete' | 'unissue' | 'issue';

interface UseSalesOrderBulkActionsReturn {
  executeBulkAction: (actionKey: SalesOrderBulkActionKey) => Promise<void>;
  isLoading: boolean;
  error: any | null;
}

const serviceMap: Record<
  SalesOrderBulkActionKey,
  (ids: number[]) => Promise<any>
> = {
  delete: deleteSalesOrdersService,
  unissue: unIssueSalesOrdersService,
  issue: issueSalesOrdersService,
};

export function useSalesOrderBulkActions({
  selectedIds,
  fetchSearchResult,
  onSuccess,
  onError,
}: UseSalesOrderBulkActionsProps): UseSalesOrderBulkActionsReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any | null>(null);

  const handleAction = useCallback(
    async (
      actionFn: (ids: number[]) => Promise<any>,
      actionType: SalesOrderBulkActionKey
    ) => {
      if (selectedIds.length === 0) {
        const err = new Error(
          `No sales orders selected for bulk ${actionType}.`
        );
        setError(err);
        onError?.(err, actionType);
        return;
      }
      setIsLoading(true);
      try {
        await actionFn(selectedIds);
        await fetchSearchResult();
        onSuccess?.();
      } catch (err) {
        logErrorCtx(`Error during bulk ${actionType} sales orders`, {
          error: err instanceof Error ? err : new Error(String(err)),
          description: `Failed to ${actionType} ${selectedIds.length} sales orders`,
        });
        setError(err);
        onError?.(err, actionType);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedIds, fetchSearchResult, onSuccess, onError]
  );

  const executeBulkAction = useCallback(
    async (actionKey: SalesOrderBulkActionKey) => {
      setError(null);
      const serviceFunction = serviceMap[actionKey];
      if (serviceFunction) {
        await handleAction(serviceFunction, actionKey);
      } else {
        const err = new Error(`Invalid action key: ${actionKey}`);
        console.error(
          `No service function mapped for action key: ${actionKey}`
        );
        setError(err);
        onError?.(err, actionKey);
      }
    },
    [handleAction, onError]
  );

  return {
    executeBulkAction,
    isLoading,
    error,
  };
}
